import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { FileText, AlertTriangle, CheckCircle2, Download, FileSpreadsheet, Search, Eye } from 'lucide-react';
import OFACEntityLookup from '@/components/OFACEntityLookup';
import EntityDetailModal from '@/components/EntityDetailModal';
import { MatchEvidence, ScreeningTracePanel } from '@/components/MatchEvidence';
import AdverseMediaPanel from '@/components/AdverseMediaPanel';
import { groupMatchesByEntity } from '@/lib/groupMatches';
import { matchedNameType, nameTypeClass, listingIdentity, groupNameClassifications } from '@/lib/nameTypes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

export default function ResultsPage({ user, onLogout }) {
  const location = useLocation();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScan, setSelectedScan] = useState(null);
  const [showEntityLookup, setShowEntityLookup] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [relatedListings, setRelatedListings] = useState([]);
  // Today's state of the records behind a saved result. Checked on demand when
  // a scan is opened; it never touches the frozen screening explanation.
  const [listingStatus, setListingStatus] = useState({});
  const [listingStatusFailed, setListingStatusFailed] = useState(false);

  useEffect(() => {
    if (!selectedScan?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/scans/${selectedScan.id}/listing-status`, {
          headers: getAuthHeaders(),
        });
        if (!cancelled) { setListingStatus(res.data); setListingStatusFailed(false); }
      } catch (error) {
        if (!cancelled) { setListingStatus({}); setListingStatusFailed(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedScan?.id]);

  const statusForListing = useCallback((match) => {
    const { recordId, authority } = listingIdentity(match);
    return (listingStatus.statuses || []).find(
      (s) => s.listing_id === recordId && s.source === authority
    );
  }, [listingStatus]);

  // Open one exact listing. The clicked record is what the panel resolves; its
  // siblings are offered separately as related listings, never substituted.
  const openListing = useCallback((listing, siblings = []) => {
    setSelectedMatch(listing);
    setRelatedListings(siblings);
  }, []);

  // One card per entity, naming every list it appears on. The API payload is
  // untouched — exports, the PDF and webhooks still carry each listing.
  const matchGroups = useMemo(
    () => groupMatchesByEntity(selectedScan?.matches || []),
    [selectedScan]
  );

  const fetchScans = useCallback(async (targetScanId = null) => {    try {
      const response = await axios.get(`${API}/scans`, { headers: getAuthHeaders() });
      setScans(response.data);
      
      // If a specific scan ID was provided, select that scan
      if (targetScanId) {
        const targetScan = response.data.find(s => s.id === targetScanId);
        if (targetScan) {
          setSelectedScan(targetScan);
        } else if (response.data.length > 0) {
          setSelectedScan(response.data[0]);
        }
      } else if (response.data.length > 0) {
        setSelectedScan(prev => prev || response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load scan results');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If navigated with a specific scan result
    if (location.state?.scanResult) {
      setSelectedScan(location.state.scanResult);
      fetchScans();
    } else if (location.state?.scanId) {
      fetchScans(location.state.scanId);
    } else {
      fetchScans();
    }
  }, [location, fetchScans]);

  const getRiskColor = (level) => {
    switch (level) {
      case 'high':
        return 'text-rose-700 bg-rose-50 border-rose-200';
      case 'medium':
        return 'text-amber-700 bg-amber-50 border-amber-200';
      case 'low':
        return 'text-blue-700 bg-blue-50 border-blue-200';
      default:
        return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'bg-rose-600';
    if (score >= 70) return 'bg-amber-500';
    return 'bg-blue-500';
  };

  const downloadReportPdf = async (scan) => {
    if (!scan?.id) return;
    try {
      const response = await axios.get(`${API}/scans/${scan.id}/report.pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compliance-report-${scan.id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Compliance report downloaded');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to download report';
      toast.error(typeof detail === 'string' ? detail : 'Failed to download report');
    }
  };

  const handleExport = async (format) => {    try {
      const response = await axios.get(`${API}/reports/export/${format}?days=30`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compliance_report_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${format.toUpperCase()} report downloaded successfully`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()} report`);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} activePage="results">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="results-page-title">
              Scan Results
            </h1>
            <p className="text-sm text-slate-600 mt-2">Detailed screening results and match analysis</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setShowEntityLookup(true)}
              variant="outline"
              className="border-slate-300"
              data-testid="ofac-lookup-button"
            >
              <Search className="w-4 h-4 mr-2" />
              OFAC Lookup
            </Button>
            {(user.role === 'admin' || user.role === 'analyst') && scans.length > 0 && (
              <>
                <Button
                  onClick={() => handleExport('csv')}
                  variant="outline"
                  className="border-slate-300"
                  data-testid="export-csv-button"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button
                  onClick={() => handleExport('pdf')}
                  variant="outline"
                  className="border-slate-300"
                  data-testid="export-pdf-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </>
            )}
          </div>
        </div>

        {/* OFAC Entity Lookup Modal */}
        {showEntityLookup && (
          <OFACEntityLookup onClose={() => setShowEntityLookup(false)} />
        )}

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading results...</div>
        ) : scans.length === 0 ? (
          <Card className="p-12 text-center border border-slate-200">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No scan results yet. Run your first scan to see results here.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Scans List */}
            <div className="lg:col-span-4">
              <Card className="border border-slate-200 shadow-sm">
                <div className="p-4 border-b border-slate-200">
                  <h2 className="font-semibold text-slate-900">Recent Scans</h2>
                </div>
                <div className="divide-y divide-slate-200 max-h-[600px] overflow-y-auto">
                  {scans.map((scan) => (
                    <div
                      key={scan.id}
                      onClick={() => setSelectedScan(scan)}
                      className={`p-4 cursor-pointer transition-colors hover:bg-slate-50 ${
                        selectedScan?.id === scan.id ? 'bg-slate-50' : ''
                      }`}
                      data-testid={`scan-item-${scan.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-medium text-slate-900 text-sm">{scan.query_name}</p>
                        <Badge
                          className={`text-xs ${getRiskColor(scan.risk_level)}`}
                          data-testid={`risk-badge-${scan.risk_level}`}
                        >
                          {scan.risk_level}
                        </Badge>
                      </div>
                      <p className="text-xs font-mono text-slate-500">
                        {new Date(scan.scan_date).toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-600 mt-1">{scan.matches.length} matches found</p>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Scan Details */}
            <div className="lg:col-span-8">
              {selectedScan && (
                <div className="space-y-6" data-testid="scan-details">
                  {/* Overview */}
                  <Card className="border border-slate-200 shadow-sm">
                    <div className="p-6">
                      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                        <div className="min-w-0">
                          <h2 className="text-2xl font-bold text-slate-900 break-words" data-testid="scan-query-name">
                            {selectedScan.query_name}
                          </h2>
                          <p className="text-sm font-mono text-slate-500 mt-1">
                            {new Date(selectedScan.scan_date).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadReportPdf(selectedScan)}
                            data-testid="download-report-pdf-btn"
                            className="gap-1"
                          >
                            <Download className="w-4 h-4" />
                            Report PDF
                          </Button>
                          <Badge
                            className={`text-base md:text-lg px-3 py-1.5 md:px-4 md:py-2 ${getRiskColor(selectedScan.risk_level)}`}
                            data-testid="selected-scan-risk-badge"
                          >
                            {selectedScan.risk_level.toUpperCase()}
                          </Badge>
                        </div>
                      </div>

                      {selectedScan.risk_level === 'clear' ? (
                        <div className="flex items-center p-4 bg-emerald-50 border border-emerald-200 rounded-md">
                          <CheckCircle2 className="w-5 h-5 text-emerald-600 mr-3" />
                          <p className="text-sm text-emerald-700">No matches found. This entity is clear.</p>
                        </div>
                      ) : (
                        <div className="flex items-center p-4 bg-amber-50 border border-amber-200 rounded-md">
                          <AlertTriangle className="w-5 h-5 text-amber-600 mr-3" />
                          <p className="text-sm text-amber-700">
                            {matchGroups.length} potential match(es) detected
                            {selectedScan.matches.length > matchGroups.length &&
                              ` — ${selectedScan.matches.length} listings across ${matchGroups.length} ${matchGroups.length === 1 ? 'entity' : 'entities'}`}
                          </p>
                        </div>
                      )}
                      {listingStatusFailed && (
                        <p
                          className="mt-3 text-xs text-slate-500"
                          data-testid="listing-status-unavailable"
                        >
                          The check against today's watchlist is unavailable, so a listing that has
                          since been delisted or renamed will not be flagged on these cards.
                        </p>
                      )}
                      {selectedScan.screening_trace && (
                        <div className="mt-4">
                          <ScreeningTracePanel trace={selectedScan.screening_trace} />
                        </div>
                      )}
                    </div>
                  </Card>

                  {/* Adverse media — its own domain, its own evidence */}
                  <AdverseMediaPanel
                    subjectName={selectedScan.query_name}
                    scanId={selectedScan.id}
                    canReview={['super_admin', 'org_admin', 'compliance_reviewer'].includes(user?.role)}
                  />

                  {/* Matches */}
                  {selectedScan.matches.length > 0 && (
                    <Card className="border border-slate-200 shadow-sm">
                      <div className="p-6 border-b border-slate-200">
                        <h3 className="text-xl font-semibold text-slate-900">Match Details</h3>
                        <p className="text-sm text-slate-500 mt-1">One card per entity — click it to view full details</p>
                      </div>
                      <div className="divide-y divide-slate-200">
                        {matchGroups.map(({ key, primary: match, sources, listings }, idx) => {
                          // The UN listing facts can sit on any of the group's
                          // listings, not necessarily the best-scoring one.
                          const unListed = listings.find(
                            (l) => l.details?.un_listing && Object.keys(l.details.un_listing).length > 0
                          );
                          return (
                          <div 
                            key={key} 
                            className="p-6 hover:bg-slate-50 cursor-pointer transition-colors" 
                            data-testid={`match-item-${idx}`}
                            onClick={() => openListing(match, listings)}
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="font-semibold text-slate-900 text-lg">{match.matched_name}</h4>
                                  <Badge
                                    variant="outline"
                                    className={`text-[10px] border ${nameTypeClass(matchedNameType(match))}`}
                                    data-testid={`matched-name-type-${idx}`}
                                  >
                                    {matchedNameType(match)}
                                  </Badge>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-blue-600 hover:text-blue-800"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openListing(match, listings);
                                    }}
                                    data-testid={`view-record-${idx}`}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    View record
                                  </Button>
                                </div>
                                <p
                                  className="text-xs text-slate-500 font-mono mt-1"
                                  data-testid={`match-record-identity-${idx}`}
                                >
                                  {listingIdentity(match).authority || 'source not recorded'} · listing{' '}
                                  {listingIdentity(match).recordId || 'ID not recorded'}
                                </p>
                                {(() => {
                                  const status = statusForListing(match);
                                  if (!status || status.status === 'unchanged') return null;
                                  const tone = status.status === 'delisted'
                                    ? 'bg-sky-50 text-sky-800 border-sky-200'
                                    : status.status === 'renamed'
                                      ? 'bg-amber-50 text-amber-800 border-amber-200'
                                      : 'bg-slate-50 text-slate-600 border-slate-200';
                                  const label = status.status === 'delisted'
                                    ? 'Delisted since screening'
                                    : status.status === 'renamed'
                                      ? 'Renamed since screening'
                                      : 'Cannot be re-checked';
                                  return (
                                    <div
                                      className={`mt-2 inline-flex items-start gap-2 px-2.5 py-1.5 rounded border text-xs ${tone}`}
                                      data-testid={`listing-status-${idx}`}
                                    >
                                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                      <span>
                                        <span className="font-semibold">{label}.</span>{' '}
                                        {status.detail}
                                      </span>
                                    </div>
                                  );
                                })()}
                                <div className="flex items-center gap-2 mt-1 flex-wrap" data-testid={`match-sources-${idx}`}>
                                  <span className="text-sm text-slate-600">
                                    {sources.length > 1 ? 'Listed by:' : 'Source:'}
                                  </span>
                                  {sources.map((source) => (
                                    <Badge
                                      key={`src-${key}-${source}`}
                                      variant="outline"
                                      className="text-xs border-slate-300 text-slate-700"
                                      data-testid={`match-source-badge-${idx}-${source.replace(/\s+/g, '-').toLowerCase()}`}
                                    >
                                      {source}
                                    </Badge>
                                  ))}
                                  {listings.length > 1 && (
                                    <span className="text-xs text-slate-400">
                                      {listings.length} listings
                                    </span>
                                  )}
                                </div>
                                {(match.identity_verification?.sanctions_listing ||
                                  match.identity_verification?.verdict === 'SANCTIONS_LIST_HIT') && (
                                  <div
                                    className="inline-flex items-center gap-2 mt-2 px-3 py-1 rounded-sm bg-rose-600 text-white text-xs font-bold uppercase tracking-wider"
                                    data-testid={`sanctions-high-marker-${idx}`}
                                  >
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    High — sanctions listing
                                  </div>
                                )}
                                {unListed && (
                                  <div
                                    className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 p-3 bg-slate-50 border border-slate-200 rounded"
                                    data-testid={`un-listing-facts-${idx}`}
                                  >
                                    {[
                                      ['List', unListed.source],
                                      ['Reference', unListed.details.un_listing.unsc_id],
                                      ['Resolution', unListed.details.un_listing.un_resolution],
                                      ['First listed', unListed.details.un_listing.listing_date],
                                      ['Last amended', unListed.details.un_listing.last_amended],
                                      ['List data as of', (unListed.details.list_data_as_of || '').slice(0, 10)],
                                    ].filter(([, value]) => value).map(([label, value]) => (
                                      <div key={`fact-${label}-${idx}`}>
                                        <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
                                        <p className="text-xs font-medium text-slate-800">{value}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <MatchEvidence match={match} index={idx} />
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <Badge className="text-xs">{match.match_type}</Badge>
                                  {match.details?.gender && (
                                    <Badge variant="outline" className="text-xs">Gender: {match.details.gender}</Badge>
                                  )}
                                  {match.details?.birth_date && (
                                    <Badge variant="outline" className="text-xs">DOB: {match.details.birth_date}</Badge>
                                  )}
                                  {(match.details?.nationality?.length > 0 || match.details?.nationalities?.length > 0) && (
                                    <Badge variant="outline" className="text-xs">
                                      Nationality: {(match.details.nationality || match.details.nationalities || []).join(', ')}
                                    </Badge>
                                  )}
                                  {match.details?.programs?.length > 0 && (
                                    <Badge className="bg-rose-100 text-rose-800 text-xs">
                                      {match.details.programs.join(', ')}
                                    </Badge>
                                  )}
                                  {/* UNSCR program tags */}
                                  {match.details?.program_ids?.length > 0 && (
                                    match.details.program_ids.map((pid, pidx) => (
                                      <Badge key={`pid-${pid}-${pidx}`} className="bg-orange-100 text-orange-800 border border-orange-200 text-xs font-mono" data-testid={`program-tag-${pidx}`}>
                                        {pid}
                                      </Badge>
                                    ))
                                  )}
                                  {match.details?.sanctions_details?.length > 0 && (
                                    match.details.sanctions_details.map((sd, sdx) => (
                                      sd.program && (
                                        <Badge key={`sd-${sd.program}-${sdx}`} className="bg-red-100 text-red-800 border border-red-200 text-xs" data-testid={`sanction-program-${sdx}`}>
                                          {sd.program}{sd.listing_date ? ` (${sd.listing_date})` : ''}
                                        </Badge>
                                      )
                                    ))
                                  )}
                                </div>
                                {(() => {
                                  const groups = groupNameClassifications(
                                    match.details?.name_classifications || []
                                  ).filter((g) => g.type !== 'Primary name');
                                  if (groups.length === 0) return null;
                                  return (
                                    <div className="mt-2 space-y-1" data-testid={`match-name-types-${idx}`}>
                                      {groups.map(({ type, names }) => (
                                        <p key={`names-${idx}-${type}`} className="text-sm text-slate-500">
                                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 mr-2">
                                            {type}
                                          </span>
                                          {names.slice(0, 3).join(', ')}
                                          {names.length > 3 && ` +${names.length - 3} more`}
                                        </p>
                                      ))}
                                    </div>
                                  );
                                })()}
                                {listings.length > 1 && (
                                  <div
                                    className="mt-3 border border-slate-200 rounded divide-y divide-slate-200"
                                    data-testid={`match-listings-${idx}`}
                                  >
                                    <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 bg-slate-50">
                                      Listings for this party — open the one you are reviewing
                                    </p>
                                    {listings.map((listing, lidx) => {
                                      const ident = listingIdentity(listing);
                                      return (
                                        <div
                                          key={`listing-${key}-${ident.authority}-${ident.recordId}-${lidx}`}
                                          className="px-3 py-2 flex items-center justify-between gap-3"
                                          data-testid={`match-listing-${idx}-${lidx}`}
                                        >
                                          <div className="min-w-0">
                                            <p className="text-sm text-slate-800 truncate">{listing.matched_name}</p>
                                            <p className="text-xs text-slate-500 font-mono">
                                              {ident.authority || 'source not recorded'} · listing{' '}
                                              {ident.recordId || 'ID not recorded'}
                                            </p>
                                          </div>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="shrink-0"
                                            onClick={(e) => { e.stopPropagation(); openListing(listing, listings); }}
                                            data-testid={`open-listing-${idx}-${lidx}`}
                                          >
                                            View record
                                          </Button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Match Score */}
                            <div className="mt-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-slate-700">Match Confidence</span>
                                <span
                                  className="text-sm font-mono font-semibold text-slate-900"
                                  data-testid={`match-score-${idx}`}
                                >
                                  {match.match_score ?? match.score ?? 0}%
                                </span>
                              </div>
                              <Progress value={match.match_score ?? match.score ?? 0} className="h-2" indicatorClassName={getScoreColor(match.match_score ?? match.score ?? 0)} />
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </Card>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Entity Detail Modal — always the exact listing that was clicked */}
        {selectedMatch && (
          <EntityDetailModal
            entityId={listingIdentity(selectedMatch).recordId}
            source={listingIdentity(selectedMatch).authority}
            matchData={selectedMatch}
            listingStatus={statusForListing(selectedMatch)}
            relatedListings={relatedListings}
            onOpenListing={(listing) => openListing(listing, relatedListings)}
            onClose={() => { setSelectedMatch(null); setRelatedListings([]); }}
          />
        )}
      </div>
    </Layout>
  );
}